// REST API クライアント。
// 認証モードに応じた ApiRequest を注入し、required 環境では Bearer トークン付きで呼び出す。
// JSON 不一致など本来発生しないケースは throw して TanStack Query 側で扱う。

import { useMemo } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useApiRequest, type ApiRequest } from "../auth/api-request-context";

interface BootstrapResponse {
  canvasId: string;
  serverSeq: number;
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
    isFavorite?: boolean | undefined;
  };
  objects: readonly CanvasObject[];
}

export interface RecentCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  lastOpenedAt: string;
}

export interface FavoriteCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
  favoritedAt: string;
}

export interface AllCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
}

/** GET /api/me のレスポンス（権限フラグを含むアクター情報）。 */
export interface MeResponse {
  actor: {
    userId: string;
    displayName: string;
    permissions: {
      canRead: boolean;
      canWrite: boolean;
      canAdmin: boolean;
    };
  };
}

export interface OgFetchResult {
  url: string;
  title?: string | undefined;
  description?: string | undefined;
  image?: string | undefined;
  siteName?: string | undefined;
}

export interface RealtimeSessionTicket {
  token: string;
  expiresAt: string;
}

interface PresignedUpload {
  uploadUrl: string;
  bucket: string;
  key: string;
  expiresInSeconds: number;
}

interface UpdateCanvasMetaResponse {
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
  };
}

export interface CanvasApiClient {
  readonly fetchCanvasBootstrap: (canvasId: string) => Promise<BootstrapResponse>;
  readonly fetchRecentCanvases: () => Promise<{ items: readonly RecentCanvasItem[] }>;
  readonly deleteRecentCanvas: (canvasId: string) => Promise<void>;
  /** 現在ログインしているユーザーの権限情報を取得する（管理者判定に使う）。 */
  readonly fetchMe: () => Promise<MeResponse>;
  /** ログインユーザーのお気に入りキャンバス一覧（登録日時の新しい順）。 */
  readonly fetchFavorites: () => Promise<{ items: readonly FavoriteCanvasItem[] }>;
  /** 現在のキャンバスをお気に入り登録する。 */
  readonly addFavorite: (canvasId: string) => Promise<void>;
  /** 現在のキャンバスのお気に入りを解除する。 */
  readonly removeFavorite: (canvasId: string) => Promise<void>;
  /** 管理者専用: 全キャンバス一覧（更新日時の新しい順・最大 48 件）。 */
  readonly fetchAllCanvases: () => Promise<{ items: readonly AllCanvasItem[] }>;
  readonly updateCanvasMeta: (
    canvasId: string,
    input: { title?: string | undefined; thumbnailKey?: string | null | undefined }
  ) => Promise<UpdateCanvasMetaResponse>;
  readonly createCanvasObject: (
    canvasId: string,
    object: CanvasObject
  ) => Promise<{ object: CanvasObject }>;
  readonly patchCanvasObject: (
    canvasId: string,
    objectId: string,
    input: PatchObjectInput
  ) => Promise<{ object: CanvasObject }>;
  readonly deleteCanvasObject: (canvasId: string, objectId: string) => Promise<void>;
  readonly presignImageUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignVideoUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignThumbnailUpload: (params: {
    canvasId: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly notifyUploadComplete: (
    canvasId: string,
    bucket: string,
    key: string
  ) => Promise<UploadCompleteResult>;
  readonly fetchOgp: (url: string) => Promise<OgFetchResult>;
  readonly issueRealtimeSession: (canvasId: string) => Promise<RealtimeSessionTicket>;
}

async function callJson<T>(request: ApiRequest, path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init);
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${body || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ---- canvas ----

export interface PatchObjectInput {
  transform?:
    | {
        x?: number | undefined;
        y?: number | undefined;
        w?: number | undefined;
        h?: number | undefined;
        rotation?: number | undefined;
        zOrder?: number | undefined;
      }
    | undefined;
  text?: string | undefined;
}

export interface UploadCompleteResult {
  ok: boolean;
  /** CloudFront 経由の公開 URL。未設定（ATTACHMENTS_PUBLIC_BASE_URL 未指定）の場合は null。 */
  publicUrl: string | null;
}

export function createCanvasApiClient(request: ApiRequest): CanvasApiClient {
  return {
    fetchCanvasBootstrap(canvasId) {
      return callJson<BootstrapResponse>(request, `/canvases/${encodeURIComponent(canvasId)}`);
    },
    fetchRecentCanvases() {
      return callJson<{ items: readonly RecentCanvasItem[] }>(request, "/me/recent-canvases");
    },
    deleteRecentCanvas(canvasId) {
      return callJson<void>(request, `/me/recent-canvases/${encodeURIComponent(canvasId)}`, {
        method: "DELETE",
      });
    },
    fetchMe() {
      return callJson<MeResponse>(request, "/me");
    },
    fetchFavorites() {
      return callJson<{ items: readonly FavoriteCanvasItem[] }>(request, "/me/favorites");
    },
    addFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "PUT",
      });
    },
    removeFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "DELETE",
      });
    },
    fetchAllCanvases() {
      return callJson<{ items: readonly AllCanvasItem[] }>(request, "/admin/canvases");
    },
    updateCanvasMeta(canvasId, input) {
      return callJson<UpdateCanvasMetaResponse>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}`,
        { method: "PUT", body: JSON.stringify(input) }
      );
    },
    createCanvasObject(canvasId, object) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects`,
        { method: "POST", body: JSON.stringify({ object }) }
      );
    },
    patchCanvasObject(canvasId, objectId, input) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "PATCH", body: JSON.stringify(input) }
      );
    },
    deleteCanvasObject(canvasId, objectId) {
      return callJson<void>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "DELETE" }
      );
    },
    presignImageUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:image`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignVideoUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:video`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignThumbnailUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/thumbnail`,
        {
          method: "POST",
          body: JSON.stringify({ contentLength: params.contentLength }),
        }
      );
    },
    notifyUploadComplete(canvasId, bucket, key) {
      return callJson<UploadCompleteResult>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/uploads:complete`,
        {
          method: "POST",
          body: JSON.stringify({ bucket, key }),
        }
      );
    },
    fetchOgp(url) {
      return callJson<OgFetchResult>(request, `/og?url=${encodeURIComponent(url)}`);
    },
    issueRealtimeSession(canvasId) {
      return callJson<RealtimeSessionTicket>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/realtime-session`
      );
    },
  };
}

export function useCanvasApi(): CanvasApiClient {
  const request = useApiRequest();
  return useMemo(() => createCanvasApiClient(request), [request]);
}
